import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { JOBS } from "../data/jobs";

/**
 * "Recent Work" gallery — a bento of curated jobs, each tile links to /work/:slug.
 * Sits between Manifesto and Praise.
 */
export default function Gallery() {
    return (
        <section
            id="work"
            data-testid="gallery"
            className="relative bg-black py-24 md:py-40 overflow-hidden"
        >
            <div className="px-6 md:px-16">
                <div className="grid grid-cols-12 gap-6 mb-12 md:mb-16">
                    <div className="col-span-12 md:col-span-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 pt-2">
                        [ Ch. 04 · b ]
                        <br />
                        Recent Work
                    </div>
                    <h2 className="col-span-12 md:col-span-10 font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] tracking-tight">
                        Every job is a
                        <br />
                        <span className="rainbow-text">case study.</span>
                    </h2>
                </div>

                <div
                    data-testid="gallery-grid"
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
                >
                    {JOBS.map((job, i) => (
                        <motion.div
                            key={job.slug}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{
                                duration: 0.9,
                                delay: (i % 3) * 0.08,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                        >
                            <Link
                                to={`/work/${job.slug}`}
                                data-testid={`job-card-${job.slug}`}
                                className="group block relative overflow-hidden border border-white/10 hover:border-white/40 transition-colors aspect-[4/5]"
                            >
                                <img
                                    src={job.hero}
                                    alt={job.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-[1.04]"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                                {/* Corner service tag */}
                                <div className="absolute top-4 left-4 font-mono text-[9px] tracking-[0.3em] uppercase text-white/90 border border-white/40 glass-chip px-2.5 py-1">
                                    {job.service}
                                </div>

                                {/* Color chips row */}
                                {job.colors.length > 0 && (
                                    <div className="absolute top-4 right-4 flex gap-1.5">
                                        {job.colors.slice(0, 3).map((c) => (
                                            <span
                                                key={c.code}
                                                className="w-5 h-5 border border-white/40 shadow-md"
                                                style={{ background: c.hex }}
                                                aria-label={`${c.brand} ${c.name}`}
                                            />
                                        ))}
                                    </div>
                                )}

                                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#FFE600]">
                                        {job.location} · {job.duration}
                                    </div>
                                    <h3 className="font-display uppercase text-2xl md:text-3xl leading-[0.95] tracking-tight mt-2">
                                        {job.title}
                                    </h3>
                                    <div className="mt-4 flex items-center justify-between">
                                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/60">
                                            {job.year}
                                        </div>
                                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/90 group-hover:text-[#FFE600] transition-colors">
                                            See the job →
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
